import { Navigate, Route } from 'react-router-dom';
import { PATH } from './PATH';
import { PublicRoutes } from './PublicRoutes';
import { SecuredRoutes } from './SecuredRoutes';
import {
    AnalyticsFinancialPositionPage,
    AnalyticsPage,
    AnalyticsReportingPage,
    AttributesPage,
    BooksChartOfAccountsPage,
    BooksJournalPage,
    BooksPage,
    BooksTrialBalancePage,
    BooksVatReportPage,
    BranchesListPage,
    BranchesPage,
    BranchesShiftsPage,
    BranchImportPage,
    BrandsPage,
    CategoriesPage,
    ConfigurationsPage,
    ConfigurationsPeriodsPage,
    ConfigurationsSettingsPage,
    ControlCenterPage,
    CreateProduct,
    EditProduct,
    FileImport,
    Login,
    ManualImport,
    NotFound,
    OnboardingPage,
    OperationsActivityPage,
    OperationsCategoriesPage,
    OperationsEditorPage,
    OperationsPage,
    OperationsRecordPage,
    OperationsTemplatesPage,
    ProductDetail,
    ProductsPage,
    Purchases,
    RegistersDividendsPage,
    RegistersFixedAssetsPage,
    RegistersPage,
    RegistersSalariesPage,
    Sales,
    SetupPendingPage,
    StatementsBalanceSheetPage,
    StatementsPage,
    StatementsProfitLossPage,
    SuppliersPage,
    TeamPage,
} from '@/pages';

export const AppRouter = (
    <Route>
        <Route element={<NotFound />} path={PATH.NOT_FOUND} />
        {/* Public: the login page redirects to the dashboard itself when the
            visitor is already authenticated. */}
        <Route element={<PublicRoutes />}>
            <Route element={<Login />} path={PATH.LOGIN} />
        </Route>
        {/* Secured: every data screen sits behind hasRefreshableSession(). HOME is
            the Control Center — the same element on both of its paths rather
            than a redirect from one to the other, so `/` and `/control-center`
            both work and neither rewrites the address bar. */}
        <Route element={<SecuredRoutes />}>
            {/* Full-screen: `useLayout` gives these no sidebar or navbar.
                The wizard's splat is its steps, settled by the wizard frame. */}
            <Route element={<OnboardingPage />} path={`${PATH.ONBOARDING}/*`} />
            <Route element={<SetupPendingPage />} path={PATH.SETUP_PENDING} />
            <Route element={<ControlCenterPage />} path={PATH.HOME} />
            <Route element={<ControlCenterPage />} path={PATH.CONTROL_CENTER} />
            <Route element={<Sales />} path={PATH.SALES} />
            {/* Where the shift register used to live. A redirect rather than a
                page: the URL is in bookmarks and support threads, and sending
                someone to the screen it moved to is a better answer than a 404.
                `replace` so Back returns to wherever they came from rather than
                bouncing off the old URL again. */}
            <Route
                element={<Navigate replace to={PATH.BRANCHES.SHIFTS} />}
                path={PATH.LEGACY_SALES_SHIFTS}
            />
            {/* Analytics: the same tab shell the /accounting screens use, on
                its own nav item. Reporting is still a placeholder page, routed
                from the start so the bar's shape is settled rather than
                growing under people later. The index route makes the bare
                shell path the Financial Position tab without rewriting the
                URL. */}
            <Route element={<AnalyticsPage />} path={PATH.ANALYTICS.ROOT}>
                <Route element={<AnalyticsFinancialPositionPage />} index />
                <Route
                    element={<AnalyticsFinancialPositionPage />}
                    path={PATH.ANALYTICS.FINANCIAL_POSITION}
                />
                <Route
                    element={<AnalyticsReportingPage />}
                    path={PATH.ANALYTICS.REPORTING}
                />
            </Route>
            <Route element={<Purchases />} path={PATH.PURCHASES.ROOT} />
            <Route
                element={<ManualImport />}
                path={PATH.PURCHASES.MANUAL_IMPORT}
            />
            <Route
                element={<ManualImport />}
                path={PATH.PURCHASES.MANUAL_IMPORT_EDIT}
            />
            <Route element={<FileImport />} path={PATH.PURCHASES.FILE_IMPORT} />
            {/* The Branches shell: the branches that exist, and the shifts run
                at them. Same shape as the accounting shells — one shell for
                every URL under it, each tab a child route — with one
                difference: the default tab has *only* the index route, so
                `/branches` is the branch list's single address. See
                `PATH.BRANCHES`. */}
            <Route element={<BranchesPage />} path={PATH.BRANCHES.ROOT}>
                <Route element={<BranchesListPage />} index />
                <Route
                    element={<BranchesShiftsPage />}
                    path={PATH.BRANCHES.SHIFTS}
                />
            </Route>
            {/* One branch's catalog import, outside the Branches shell for
                manual import's reason: a multi-step form under a tab bar
                offers ways to lose it. Each step is a segment under it
                (`PATH.BRANCHES.IMPORT_STEP`). Its back button is registered
                in `navigation.ts`. */}
            <Route
                element={<BranchImportPage />}
                path={`${PATH.BRANCHES.IMPORT}/*`}
            />
            <Route element={<ProductsPage />} path={PATH.PRODUCTS.ROOT} />
            <Route element={<CreateProduct />} path={PATH.PRODUCTS.NEW} />
            <Route element={<EditProduct />} path={PATH.PRODUCTS.EDIT} />
            <Route element={<ProductDetail />} path={PATH.PRODUCTS.DETAIL} />
            <Route element={<CategoriesPage />} path={PATH.CATEGORIES} />
            <Route element={<AttributesPage />} path={PATH.ATTRIBUTES} />
            <Route element={<SuppliersPage />} path={PATH.SUPPLIERS} />
            <Route element={<BrandsPage />} path={PATH.BRANDS} />
            {/* The first accounting shell. One shell for every /accounting/books* URL;
                the tab bar lives in the shell and each tab is a child route, so
                the selected tab and the address bar are the same fact. The
                index route makes the bare shell path the Chart of Accounts tab
                without rewriting the URL. */}
            <Route element={<BooksPage />} path={PATH.ACCOUNTING.BOOKS.ROOT}>
                <Route element={<BooksChartOfAccountsPage />} index />
                <Route
                    element={<BooksChartOfAccountsPage />}
                    path={PATH.ACCOUNTING.BOOKS.ACCOUNTS}
                />
                <Route
                    element={<BooksJournalPage />}
                    path={PATH.ACCOUNTING.BOOKS.JOURNAL}
                />
                <Route
                    element={<BooksTrialBalancePage />}
                    path={PATH.ACCOUNTING.BOOKS.TRIAL_BALANCE}
                />
                <Route
                    element={<BooksVatReportPage />}
                    path={PATH.ACCOUNTING.BOOKS.VAT}
                />
            </Route>
            {/* The fourth accounting shell: the published statements. The
                shell's range is read as-of by the Balance Sheet and across by
                Profit & Loss — see `statementsOutletContext`. */}
            <Route
                element={<StatementsPage />}
                path={PATH.ACCOUNTING.STATEMENTS.ROOT}
            >
                <Route element={<StatementsBalanceSheetPage />} index />
                <Route
                    element={<StatementsBalanceSheetPage />}
                    path={PATH.ACCOUNTING.STATEMENTS.BALANCE_SHEET}
                />
                <Route
                    element={<StatementsProfitLossPage />}
                    path={PATH.ACCOUNTING.STATEMENTS.PROFIT_LOSS}
                />
            </Route>
            {/* The third accounting shell: the subledgers. Same shape as the
                other two — one shell for every URL under it, each tab a child
                route, and an index route so the bare path is Fixed Assets
                without a redirect. */}
            <Route
                element={<RegistersPage />}
                path={PATH.ACCOUNTING.REGISTERS.ROOT}
            >
                <Route element={<RegistersFixedAssetsPage />} index />
                <Route
                    element={<RegistersFixedAssetsPage />}
                    path={PATH.ACCOUNTING.REGISTERS.FIXED_ASSETS}
                />
                <Route
                    element={<RegistersSalariesPage />}
                    path={PATH.ACCOUNTING.REGISTERS.SALARIES}
                />
                <Route
                    element={<RegistersDividendsPage />}
                    path={PATH.ACCOUNTING.REGISTERS.DIVIDENDS}
                />
            </Route>
            {/* The template editor sits outside the operations shell, like
                manual import sits outside Purchases: it is a form, and a tab
                bar above a half-finished recipe offers three ways to lose it.
                The navbar's back button is registered in `navigation.ts`. */}
            <Route
                element={<OperationsEditorPage />}
                path={PATH.ACCOUNTING.OPERATIONS.EDIT}
            />
            {/* The create form — the same editor page, reading no `:key` from
                the route at all, which is what tells its hook to start blank
                rather than fetch. A separate route rather than a sentinel key
                on EDIT, for EDIT's own documented reason: its `:key` means the
                real template key everywhere downstream. */}
            <Route
                element={<OperationsEditorPage />}
                path={PATH.ACCOUNTING.OPERATIONS.NEW}
            />
            {/* No route for the version history: it is a self-served drawer
                the catalog and the editor each mount their own copy of. It
                answers a question about the screen a reader is already on, and
                a navigation made them leave that screen to ask it. The catalog
                carries `?history=<key>` so the link still survives a refresh. */}
            {/* Category management, outside the shell for the editor's reason:
                it configures the catalog rather than one operation, and a
                fourth tab would give a six-row list the weight of the catalog
                itself. Reached from the Templates tab's toolbar. */}
            <Route
                element={<OperationsCategoriesPage />}
                path={PATH.ACCOUNTING.OPERATIONS.CATEGORIES}
            />
            {/* The fifth shell: operations — the catalog of every way the
                ledger gets written to, the form that records one, and the log
                of what was recorded. Same shape as Registers; the index route
                makes the bare shell path the Templates tab without rewriting
                the URL. */}
            <Route
                element={<OperationsPage />}
                path={PATH.ACCOUNTING.OPERATIONS.ROOT}
            >
                <Route element={<OperationsTemplatesPage />} index />
                <Route
                    element={<OperationsTemplatesPage />}
                    path={PATH.ACCOUNTING.OPERATIONS.TEMPLATES}
                />
                <Route
                    element={<OperationsRecordPage />}
                    path={PATH.ACCOUNTING.OPERATIONS.RECORD}
                />
                <Route
                    element={<OperationsActivityPage />}
                    path={PATH.ACCOUNTING.OPERATIONS.ACTIVITY}
                />
            </Route>
            {/* The second accounting shell: the screens that configure the
                ledger rather than report on it. Same shape as Books — one shell
                for every URL under it, each tab a child route, and an index
                route so the bare path is the Periods tab without a redirect. */}
            <Route
                element={<ConfigurationsPage />}
                path={PATH.ACCOUNTING.CONFIGURATIONS.ROOT}
            >
                <Route element={<ConfigurationsPeriodsPage />} index />
                <Route
                    element={<ConfigurationsPeriodsPage />}
                    path={PATH.ACCOUNTING.CONFIGURATIONS.PERIODS}
                />
                <Route
                    element={<ConfigurationsSettingsPage />}
                    path={PATH.ACCOUNTING.CONFIGURATIONS.SETTINGS}
                />
            </Route>
            <Route element={<TeamPage />} path={PATH.TEAM} />
        </Route>
        <Route element={<NotFound />} path="*" />
    </Route>
);
